// components/DTHRecharge.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { apiCall } from "../utils/api";
import { APP_CONFIG } from "../app-config";

const COLORS = APP_CONFIG.colors;

const operators = [
  {
    input_sl_number: "1428",
    operator_id: "108",
    name: "Tata Play",
    logo: "TP",
    iconUrl: "https://static.mobikwik.com/appdata/operator_icons/op19.png",
    color: "#1A237E",
    bgColor: "#1A237E10",
    minLength: 10,
    maxLength: 10
  },
  {
    input_sl_number: "1425",
    operator_id: "105",
    name: "Airtel DTH",
    logo: "AD",
    iconUrl: "https://static.mobikwik.com/appdata/operator_icons/op23.png",
    color: "#E40000",
    bgColor: "#E4000010",
    minLength: 10,
    maxLength: 10
  },
  {
    input_sl_number: "1426",
    operator_id: "106",
    name: "Dish TV",
    logo: "DT",
    iconUrl: "https://static.mobikwik.com/appdata/operator_icons/op18.png",
    color: "#F57C00",
    bgColor: "#F57C0010",
    minLength: 11,
    maxLength: 11
  },
  {
    input_sl_number: "1427",
    operator_id: "107",
    name: "Sun Direct",
    logo: "SD",
    iconUrl: "https://static.mobikwik.com/appdata/operator_icons/op22.png",
    color: "#FF5722",
    bgColor: "#FF572210",
    minLength: 11,
    maxLength: 11
  },
  {
    input_sl_number: "1429",
    operator_id: "109",
    name: "Videocon D2H",
    logo: "VD",
    iconUrl: "https://static.mobikwik.com/appdata/operator_icons/op21.png",
    color: "#7B1FA2",
    bgColor: "#7B1FA210",
    minLength: 8,
    maxLength: 10
  },
];

function DTHRecharge() {
  const navigate = useNavigate();
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [subscriberId, setSubscriberId] = useState("");
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState("");

  const handleOperatorSelect = (e) => {
    const operatorId = e.target.value;
    const operator = operators.find(op => op.input_sl_number === operatorId);
    setSelectedOperator(operator || null);
    setSubscriberId("");
    setError("");
  };

  const validateSubscriberId = (id, operator) => {
    if (!operator) return "Please select an operator first";
    const len = id.length;
    if (len < operator.minLength) {
      return `Enter ${operator.minLength}-digit subscriber ID (min ${operator.minLength} digits)`;
    }
    if (len > operator.maxLength) {
      return `Enter ${operator.maxLength}-digit subscriber ID (max ${operator.maxLength} digits)`;
    }
    return null;
  };

  const handleSubscriberIdChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");

    if (selectedOperator && value.length > selectedOperator.maxLength) {
      return;
    }

    setSubscriberId(value);
    setError("");

    if (selectedOperator && value.length === selectedOperator.minLength) {
      const validationError = validateSubscriberId(value, selectedOperator);
      if (validationError) {
        setError(validationError);
      }
    }
  };

  const handleFetchDetails = async () => {
    if (!selectedOperator) {
      setError("Please select an operator first");
      return;
    }

    const validationError = validateSubscriberId(subscriberId, selectedOperator);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    setIsFetching(true);

    try {
      const result = await apiCall(
        "bill-fetch",
        {
          operator_id: selectedOperator.operator_id,
          input_sl_number: selectedOperator.input_sl_number,
          form: {
            param1: subscriberId
          }
        },
        true
      );

      if (result.status === true) {
        const billInfo = result.bill_info || {};

        navigate("/payment-confirmation", {
          state: {
            service: "DTH Recharge",
            operator: selectedOperator.name,
            operator_id: selectedOperator.operator_id,
            logo: selectedOperator.icon,
            number: billInfo.cellNumber || "",
            amount: billInfo.billnetamount,
            consumerName: billInfo.userName || "",
            serviceSpecificData: {
              range: result.range,
              dueDate: billInfo.dueDate || "A",
              balance: billInfo.balance || "0",
              form: {
                param1: subscriberId
              }
            }
          }
        });
      } else {
        toast.error(result.message || "An error occurred. Please try again.");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setIsFetching(false);
    }
  };

  const getPlaceholderText = () => {
    if (!selectedOperator) return "Enter subscriber ID";
    if (selectedOperator.minLength === selectedOperator.maxLength) {
      return `Enter ${selectedOperator.minLength}-digit subscriber ID`;
    }
    return `Enter ${selectedOperator.minLength}-${selectedOperator.maxLength} digit subscriber ID`;
  };

  return (
    <div className="space-y-4">
      <div
        className="rounded-2xl p-4 md:p-6"
        style={{
          background: COLORS.bgColor,
          boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
          border: `1px solid ${COLORS.borderColor}`,
        }}
      >
        <h3 className="text-lg font-semibold mb-4" style={{ color: COLORS.textPrimary }}>
          DTH Recharge
        </h3>

        {/* Two-column layout for Operator and Subscriber ID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: COLORS.textSecondary }}>
              Select DTH Operator
            </label>
            <select
              value={selectedOperator?.input_sl_number || ""}
              onChange={handleOperatorSelect}
              className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all duration-200 appearance-none"
              style={{
                background: COLORS.surfaceColor,
                borderColor: error && !selectedOperator ? '#EF4444' : COLORS.borderColor,
                color: COLORS.textPrimary,
                '--tw-ring-color': COLORS.primaryColor,
              }}
            >
              <option value="">Select DTH Operator</option>
              {operators.map((op) => (
                <option key={op.input_sl_number} value={op.input_sl_number}>
                  {op.name} ({op.minLength}-{op.maxLength} digits)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: COLORS.textSecondary }}>
              Subscriber ID
              {selectedOperator && (
                <span className="text-xs ml-1" style={{ color: COLORS.textSecondary }}>
                  ({selectedOperator.minLength === selectedOperator.maxLength
                    ? `${selectedOperator.minLength} digits`
                    : `${selectedOperator.minLength}-${selectedOperator.maxLength} digits`})
                </span>
              )}
            </label>
            <input
              type="tel"
              value={subscriberId}
              onChange={handleSubscriberIdChange}
              placeholder={getPlaceholderText()}
              maxLength={selectedOperator?.maxLength || 20}
              className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all duration-200"
              style={{
                background: COLORS.surfaceColor,
                borderColor: error ? '#EF4444' : COLORS.borderColor,
                color: COLORS.textPrimary,
                '--tw-ring-color': COLORS.primaryColor,
              }}
              disabled={!selectedOperator}
            />
            {error && (
              <div className="text-xs mt-1.5 font-semibold" style={{ color: '#EF4444' }}>
                {error}
              </div>
            )}
            {!error && subscriberId && selectedOperator && (
              <div className="text-[10px] mt-1.5" style={{ color: COLORS.textSecondary }}>
                {subscriberId.length}/{selectedOperator.maxLength} digits
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            {/* View Customer Details Button */}
            <button
              onClick={handleFetchDetails}
              disabled={isFetching || !selectedOperator || !subscriberId || (selectedOperator && subscriberId.length !== selectedOperator.maxLength)}
              className="w-full py-3 rounded-xl font-semibold cursor-pointer flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: COLORS.primaryColor,
                color: COLORS.textOnPrimary,
                boxShadow: `0 4px 12px ${COLORS.primaryColor}33`,
              }}
            >
              {isFetching ? (
                <div className="w-5 h-5 rounded-full animate-spin" style={{ border: `3px solid ${COLORS.textOnPrimary}33`, borderTopColor: COLORS.textOnPrimary }} />
              ) : (
                "View Customer Details"
              )}
            </button>
          </div>
          <div></div>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .animate-spin {
            animation: spin 1s linear infinite;
          }
          .hover\\:scale-\\[1\\.02\\]:hover {
            transform: scale(1.02);
          }
          .active\\:scale-95:active {
            transform: scale(0.95);
          }
        `}
      </style>
    </div>
  );
}

export default DTHRecharge;